import {
  buildHtml,
  buildLink
} from '@caritas/ui';

import { useSearchLocationsQueryQuery } from '../../generated/queries/react-queries';
import { mapImage } from '../fragments/Image.mappers';
import { usePager } from './pager';
import { useQueryParamString } from './query-string';

export const useLocationFilter = (
  langcode: string,
  viewMode: string = 'preview',
) => {
  const [filters, setFilters] = useQueryParamString('filters', '');
  let currentValues: any;
  try {
    currentValues = JSON.parse(filters);
  } catch (e) {
    currentValues = {};
  }

  const [currentPage, setCurrentPage] = useQueryParamString('page', '0');
  const filteredLocations = useSearchLocationsQueryQuery({
    input: {
      page: 0,
      // Set a big number of items per page in order to load all of them, we
      // need all of the items for the map.
      itemsPerPage: 10000,
      langcode: langcode,
      filters: {
        canton: currentValues.canton ? currentValues.canton : [],
        startDate: currentValues.startdate || '',
        endDate: currentValues.enddate || '',
        workType: currentValues.type ? currentValues.type : [],
        language: currentValues.language ? currentValues.language : [],
        remainingSpots: parseInt(currentValues.remainingSpots) || 1,
        bioLocation: currentValues.bio || undefined,
        sosLocation: currentValues.sos || undefined,
      },
    },
  });

  const itemsPerPage = 12;
  const previewModeLimit = 8;
  const {
    firstPage,
    lastPage,
    nextPage,
    previousPage,
    pages,
    hasMissingLeftPages,
    hasMissingRightPages,
  } = usePager({
    name: 'page',
    totalItems:
      viewMode === 'preview'
        ? previewModeLimit
        : filteredLocations.data?.searchLocations.total || 0,
    itemsPerPage,
    pagesToShow: 5,
    setCurrentPage,
    currentPage,
  });

  const markers = filteredLocations.data?.searchLocations.content?.length
    ? filteredLocations.data.searchLocations.content.map((item) => {
      return {
        position: {
          lat: item.locationCoordinates.lat,
          lng: item.locationCoordinates.lon,
        },
        Content: buildHtml(`<div><strong>${item.title}</strong></div>`),
        MarkerImage: mapImage({
          image: item.headerImage?.image,
          preset: 'marker_image',
        }),
        Link: buildLink({
          target: '_blank',
          rel: 'noreferrer',
          href: item.path
        }),
        pinType: item.sosLocation
          ? 'sos'
          : item.bioLocation
            ? 'bio'
            : undefined,
      };
    })
    : [];

  const sliceStart =
    viewMode === 'preview' ? 0 : parseInt(currentPage) * itemsPerPage;
  const sliceEnd =
    viewMode === 'preview'
      ? previewModeLimit
      : (parseInt(currentPage) + 1) * itemsPerPage;
  const gridItems = filteredLocations.data?.searchLocations.content?.length
    ? filteredLocations.data.searchLocations.content
      .slice(sliceStart, sliceEnd)
      .map((item) => {
        return {
          title: item.title,
          Link: buildLink({
            target: '_blank',
            rel: 'noreferrer',
            href: item.path
          }),
          description: item.introText,
          Image: mapImage({
            image: item.headerImage?.image,
            preset: 'teaser',
          }),
          bioTag: !!item.bioLocation,
          sosTag: !!item.sosLocation,
        };
      })
    : [];

  // In order to implement the "faceted"-like filters, we need to have access
  // to all the locations which match the filters, except the faceted ones
  // (canton, worktype, language, bio, sos). From this pool of locations we can
  // choose afterwards the ones that match particular configuration of filters,
  // and based on that list we compute the available filters. This will be done
  // in the calling code, so we just provide here the list of all the matched
  // locations.
  const allLocations = useSearchLocationsQueryQuery({
    input: {
      page: 0,
      // Set a big number of items per page in order to load all of them, we
      // need all of the items for the map.
      itemsPerPage: 10000,
      langcode: langcode,
      filters: {
        canton: [],
        startDate: currentValues.startdate || '',
        endDate: currentValues.enddate || '',
        workType: [],
        language: [],
        remainingSpots: parseInt(currentValues.remainingSpots) || 1,
        bioLocation: undefined,
        sosLocation: undefined,
      },
    },
  });

  return {
    markers,
    gridItems,
    setFilters,
    allLocations: allLocations.data?.searchLocations.content || [],
    currentValues,
    isLoadingData: filteredLocations.isLoading,
    hasMoreItems:
      viewMode === 'preview' && filteredLocations.data?.searchLocations.total
        ? filteredLocations.data.searchLocations.total > previewModeLimit
        : false,
    pager:
      (pages.length > 1 && {
        firstPage,
        previousPage,
        nextPage,
        lastPage,
        pages,
        hasMissingLeftPages,
        hasMissingRightPages,
      }) ||
      undefined,
  };
};
